/**
 * admin-users 域 Zod 共享契约 — root 运营账号管理
 */

import { z } from 'zod';

export const AdminRoleEnum = z.enum(['root', 'admin', 'operator', 'viewer']);

export const AdminAccountCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{2,3}$/, '账号 CODE 必须是 2–3 位英文字母');

export const UpdateAdminUserRequestSchema = z.object({
  account_code: AdminAccountCodeSchema.optional(),
  role: AdminRoleEnum.optional(),
  status: z.enum(['active', 'disabled']).optional(),
  nickname: z.string().min(1).max(64).optional(),
});
export type UpdateAdminUserRequest = z.infer<typeof UpdateAdminUserRequestSchema>;

/**
 * audit-logs 列表查询参数（GET /api/v1/audit-logs）— 服务端过滤下推
 *
 * 全部可选；筛选跨全量数据集（前端不再仅在单页内做客户端过滤）。
 * 注意：RBAC 自范围（非 root 强制 actor_id=self）在路由解析本 schema **之后** 覆盖，
 *      非 root 即便传 actor_id 也不能越权看他人记录。
 * - page：offset 分页页码（从 1 起；admin 列表默认 offset，真实总数 + 跳任意页，dev-docs 40-分页与排序）
 * - limit：每页条数，硬上限 100
 * - actor_id：操作者多选（重复 query param；root 专用维度，非 root 被 self 覆盖）。多选 inArray，前端「操作员」筛选服务端下推（不再单页客户端再筛）
 * - resource：资源类型精确匹配（users / novels / banners / ...）
 * - resource_id：操作对象精确匹配（UUID；防注入到 PG uuid 比较）
 * - q：模糊搜索，对 action OR resource 两个 text 列做 ILIKE '%q%'（不含 uuid 列）
 * - action：动作多选（重复 query param；action 无固定枚举，接受任意字符串，仅校验长度）
 * - from / to：created_at 时间窗（from ≤ created_at < to；ISO 8601 带时区偏移）
 */
export const AuditLogsListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  actor_id: z.array(z.string().uuid()).optional(),
  resource: z.string().min(1).max(64).optional(),
  resource_id: z.string().uuid().optional(),
  // 复合 / 非 UUID 对象键精确匹配（如 {app_id}:{transaction_id}）；上限 256 与写入/存储/导出统一
  resource_key: z.string().min(1).max(256).optional(),
  q: z.string().trim().min(1).max(100).optional(),
  action: z.array(z.string().min(1).max(64)).optional(),
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
});
export type AuditLogsListQuery = z.infer<typeof AuditLogsListQuerySchema>;

export type AdminAccountResponse = {
  id: string;
  email: string;
  nickname: string;
  account_code?: string | null;
  role: 'root' | 'admin' | 'operator' | 'viewer';
  status: 'pending' | 'active' | 'disabled';
  totp_enabled: boolean;
  last_login_at: string | null;
  last_login_ip: string | null;
  created_at: string;
};

export type AdminAuditLogResponse = {
  id: string;
  actor_id: string | null;
  /**
   * 操作员展示名（admin_users.nickname；账号被硬删 → null）。读端点 leftJoin 解析，
   * 前端行首展示「谁」，不再裸露 actor_id UUID。admin 自有契约新增字段（非 breaking）。
   */
  actor_name: string | null;
  action: string;
  resource: string;
  resource_id: string | null;
  /**
   * 操作对象复合 / 非 UUID 标识（如 {app_id}:{transaction_id}）；对象标识为 UUID 时 / 批量 / 无对象操作时 null。
   * 单对象操作 resource_id 与 resource_key 恰有一个非空。上限 256，与写入/存储/查询/导出统一同一约束。
   * admin 自有契约新增字段（非 breaking，dev-docs AA-运营操作日志.md）。
   */
  resource_key: string | null;
  /**
   * 操作对象展示名（按 resource 类型只读 JOIN 业务表解析：书名 / App 名 / 分类名 / 用户邮箱 …；
   * 解析不到（对象已删 / 无 id / 该 resource 无展示名）→ 回退中文资源类型标签；未知 resource → null）。
   * 前端行内展示对象名而非 raw resource + UUID。admin 自有契约新增字段（非 breaking）。
   */
  target_name: string | null;
  /**
   * diff 字段值的展示名映射（`{ 原始值(UUID): 展示名 }`）：before/after 快照里的 `*_id` 字段（app_id /
   * novel_id …）存裸 UUID，读端点只读 JOIN 业务表解析成展示名，前端 diff 按值查表友好显示、不裸露 UUID；
   * 无可解析 id → null。admin 自有契约新增字段（非 breaking）。
   */
  field_refs: Record<string, string> | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  request_id: string;
  ip: string | null;
  ua: string | null;
  status: number;
  latency_ms: number | null;
  created_at: string;
};

/** 已发布v1只返回真实人工主体；匿名审计保持在v2完整响应类型。 */
export type AdminAuditLogV1Response = Omit<AdminAuditLogResponse, 'actor_id'> & {
  actor_id: string;
};

/**
 * 操作日志筛选 facet（ADM 操作日志「操作员 / 类型」全量下拉选项，替代前端按当前页去重）。
 * 按角色收敛：非 root 仅返回自己（self actor）。actions 为全量 DISTINCT 动作；actors 含 nickname（join 解析）。
 */
export type AuditLogFacetsResponse = {
  actions: string[];
  actors: Array<{ id: string; nickname: string | null }>;
};
